// Shared fundraiser recipient-item/search projection. No organization-class input.
import { extractList } from '@givmo/api-client/list';
import type { CharityCollectionOut, CharityOut, EtfOut, FundraiserOut } from '@givmo/api-client';

export type RecipientKind = 'charity' | 'etf' | 'collection';

/** A fundraiser recipient item reduced to what the manage/wizard picker renders + the add/remove path
 *  param needs. Charities/ETFs carry a `logo_url`; a collection carries a `cover_image` instead. */
export interface RecipientItem {
  kind: RecipientKind;
  id: string;
  name: string;
  imageUrl: string | null;
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

/** The fundraiser's current recipient items (charities + ETFs + collections), flattened + tolerant of
 *  missing arrays / ids. Used to render removable chips and to drive the item-count / can-publish gate. */
export function currentRecipients(f: FundraiserOut): RecipientItem[] {
  const out: RecipientItem[] = [];
  for (const c of f.charities ?? []) {
    const id = nonEmpty(c.id);
    if (id !== null)
      out.push({
        kind: 'charity',
        id,
        name: nonEmpty(c.name) ?? 'Charity',
        imageUrl: nonEmpty(c.logo_url),
      });
  }
  for (const e of f.etfs ?? []) {
    const id = nonEmpty(e.id);
    if (id !== null)
      out.push({
        kind: 'etf',
        id,
        name: nonEmpty(e.name) ?? 'Fund',
        imageUrl: nonEmpty(e.logo_url),
      });
  }
  for (const col of f.collections ?? []) {
    const id = nonEmpty(col.id);
    if (id !== null)
      out.push({
        kind: 'collection',
        id,
        name: nonEmpty(col.name) ?? 'Collection',
        imageUrl: nonEmpty(col.cover_image),
      });
  }
  return out;
}

/** A search result reduced to what the picker renders + the add-item route's path param needs. */
export interface RecipientSearchResult {
  kind: RecipientKind;
  id: string;
  name: string;
  imageUrl: string | null;
  subtitle: string | null;
}

function mapCharityResult(c: CharityOut): RecipientSearchResult | null {
  const id = nonEmpty(c.id);
  if (id === null) return null;
  return {
    kind: 'charity',
    id,
    name: nonEmpty(c.name) ?? 'Charity',
    imageUrl: nonEmpty(c.logo_url),
    subtitle: nonEmpty(c.category),
  };
}

function mapEtfResult(e: EtfOut): RecipientSearchResult | null {
  const id = nonEmpty(e.id);
  if (id === null) return null;
  return {
    kind: 'etf',
    id,
    name: nonEmpty(e.name) ?? 'Fund',
    imageUrl: nonEmpty(e.logo_url),
    subtitle: 'Fund',
  };
}

export function extractCharityResults(body: unknown): RecipientSearchResult[] {
  return mapResults(extractList<CharityOut>(body), mapCharityResult);
}

export function extractEtfResults(body: unknown): RecipientSearchResult[] {
  return mapResults(extractList<EtfOut>(body), mapEtfResult);
}

/** Charity Collections are VALID fundraiser items (backend `POST /fundraisers/{id}/charity-collections/
 *  {collection_id}`, CC-BE7) — distinct from direct-donation targets, where collections are rejected. */
export function extractCollectionResults(body: unknown): RecipientSearchResult[] {
  return mapResults(extractList<CharityCollectionOut>(body), (col) => {
    const id = nonEmpty(col.id);
    if (id === null) return null;
    return {
      kind: 'collection',
      id,
      name: nonEmpty(col.name) ?? 'Collection',
      imageUrl: nonEmpty(col.cover_image),
      subtitle: 'Collection',
    };
  });
}

function mapResults<T>(
  raw: T[],
  map: (item: T) => RecipientSearchResult | null,
): RecipientSearchResult[] {
  const out: RecipientSearchResult[] = [];
  for (const item of raw) {
    const mapped = map(item);
    if (mapped !== null) out.push(mapped);
  }
  return out;
}
